import { useCallback, useMemo } from 'react'
import { Ban, Check, Waves } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchAnomalies, resolveAnomaly, SEC_STR as S, type AnomalyRow } from './securityMoreApi'

/**
 * A18.13 — Обнаружение аномалий. Scored anomaly signals from the LIVE
 * `GET /admin/antifraud/events` feed; block the account or clear the signal.
 */
export function AnomalyScreen() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<AnomalyRow>[]>(
    () => [
      { key: 'user', header: S.user, type: 'text' },
      { key: 'type', header: S.type, type: 'text', width: 160 },
      { key: 'score', header: S.score, type: 'risk', width: 120 },
      { key: 'reason', header: S.reason, type: 'longtext', minWidth: 200 },
      {
        key: 'status',
        header: S.status,
        type: 'enum',
        width: 140,
        options: [
          { value: 'open', label: S.openLbl, status: 'open' },
          { value: 'blocked', label: S.blocked, status: 'blocked' },
          { value: 'cleared', label: S.cleared, status: 'active' },
        ],
      },
      { key: 'created_at', header: S.created, type: 'date', width: 150 },
    ],
    [],
  )

  const resolve = useCallback(
    async (row: AnomalyRow, action: 'block' | 'clear') => {
      try {
        await resolveAnomaly(row.id, action)
        toast(action === 'block' ? S.blocked : S.cleared, { tone: 'success' })
      } catch {
        toast(S.failed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<AnomalyRow>[]>(
    () => [
      {
        id: 'block',
        label: S.block,
        icon: <Ban size={14} />,
        danger: true,
        visible: (r) => r.status === 'open',
        onSelect: (r) => resolve(r, 'block'),
      },
      {
        id: 'clear',
        label: S.clear,
        icon: <Check size={14} />,
        visible: (r) => r.status === 'open',
        onSelect: (r) => resolve(r, 'clear'),
      },
    ],
    [resolve],
  )

  const fetcher = useCallback(fetchAnomalies, [])
  return (
    <>
      <PageHeader title={S.anomaly} screenId="A18.13" />
      <DataTable<AnomalyRow>
        screenId="A18.13"
        permission={PERMISSIONS.AUDIT_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        rowActions={rowActions}
        emptyState={{ icon: <Waves size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
